import type { FC } from 'react';
import { Draggable } from 'react-beautiful-dnd';

import { DragDropContainer } from 'components/drap-drop/DrapDropContainer';
import { Thumbnail } from 'components/thumbnail';
import { Typography } from 'enable-ui';

import { TagBorder } from 'modules/dashboard/styles/common';

import { Body, CountInput, TableHeader } from '../styles/dashboard';

import { ReactComponent as CloseIcon } from 'assets/icons/close.svg';
import { ReactComponent as DrapDropIcon } from 'assets/icons/drap-drop.svg';

import type { Exercises } from '../core/type';

import { isNil } from 'lodash';

import { COLOR, COLOR_CONFIG } from 'constants/color';

import { ExerciseContentCategory } from '../core/enum';
import { CustomHeader } from './CustomHeader';

const ExerxerciseTableHeader = ({ isEditExercises }: { isEditExercises: boolean }) => {
  return (
    <TableHeader style={{ minHeight: 48, padding: '6px 0' }}>
      <div style={{ minWidth: '24px' }}></div>
      <div style={{ minWidth: '55px' }}>
        <Typography
          style={{ textAlign: 'center', color: COLOR_CONFIG['neutral-text'] }}
          weight={600}
        >
          #
        </Typography>
      </div>
      <CustomHeader
        title="Exercise"
        styleHeader={{ width: '100%', display: 'flex', flexGrow: 1, padding: '0 8px' }}
        id="name"
      />
      <CustomHeader title="" styleHeader={{ minWidth: '32px', whiteSpace: 'normal' }} id="id" />
      <CustomHeader
        title="Reps"
        styleHeader={{ minWidth: '64px', whiteSpace: 'normal', padding: '0 4px' }}
        id="reps"
      />
      <CustomHeader title="Set" styleHeader={{ minWidth: '64px', padding: '0 4px' }} id="sets" />
      <CustomHeader
        title="Duration (s)"
        styleHeader={{ minWidth: '84px', whiteSpace: 'normal', padding: '0 4px' }}
        id="duration"
      />
      <CustomHeader
        title="Rest (s)"
        styleHeader={{ minWidth: '64px', whiteSpace: 'normal', padding: '0 4px' }}
        id="restTime"
      />

      {isEditExercises ? <div style={{ minWidth: '40px' }}></div> : <></>}
    </TableHeader>
  );
};

export const ExerciseTable: FC<{
  exercises: Exercises[];
  onChangeValueSelectedExercises: (index: number, value: Record<string, string>) => void;
  onDelete: (index: number) => void;
  onDragEnd: (result: any) => void;
  isEditExercises: boolean;
}> = ({ exercises, onChangeValueSelectedExercises, onDelete, onDragEnd, isEditExercises }) => {
  const handleKeyPress = (e: any) => {
    const keyCode = e.keyCode || e.which;
    const keyValue = String.fromCharCode(keyCode);

    if ((e.target.value === '' && keyValue === '0') || keyValue === '-') {
      e.preventDefault();
      e.preventDefault();
    }
  };

  const renderBodyTable = (item: Exercises, index: number) => {
    const isWarmupOrCooldown =
      item.category.key === ExerciseContentCategory.WARMUP ||
      item.category.key === ExerciseContentCategory.COOLDOWN;

    const isWarmupOrCooldownOrCheerup =
      isWarmupOrCooldown ||
      item.category.key === ExerciseContentCategory.MIDPOINTCHEERUP ||
      item.category.key === ExerciseContentCategory.ENDINGCHEERUP;

    return (
      <Body
        $color={`${(index + 1) % 2 === 0 ? '#f9f9f9' : '#fff'}`}
        $justify={isWarmupOrCooldown || isEditExercises ? 'space-between' : 'flex-start'}
        style={{
          // borderBottom: `1px solid ${neutral['neutral-3']}`,
          padding: '12px 0',
        }}
        key={index}
      >
        <div style={{ width: '24px', padding: '4px 0  0 0 ' }}>
          {!isWarmupOrCooldown && isEditExercises && <DrapDropIcon />}
        </div>

        <div style={{ width: '55px' }}>
          <Typography
            style={{
              textAlign: 'center',
              fontSize: 18,
              lineHeight: '26px',
              color: COLOR_CONFIG['neutral-text'],
            }}
          >
            {index + 1}.
          </Typography>
        </div>
        <div
          style={{
            padding: '0px 8px',
            width: 'calc(800px - 402px)',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flexGrow: 1,
          }}
        >
          <div>
            <Thumbnail thumbnail={item.thumbnail ?? ''} />
          </div>
          <div className="flex flex-col gap-6">
            <Typography
              weight={600}
              variant="body-2"
              color={COLOR.surfaceNeutralBold['neutral-11']}
            >
              {item.name}
            </Typography>
            <div className="flex gap-6">
              <TagBorder>
                <Typography
                  className="text-center"
                  size={12}
                  height={16}
                  color={COLOR_CONFIG['neutral-text']}
                  weight={600}
                >
                  {item?.category?.name}
                </Typography>
              </TagBorder>
              {!isWarmupOrCooldownOrCheerup && (
                <>
                  {item.level && (
                    <TagBorder>
                      <Typography
                        className="text-center"
                        size={12}
                        height={16}
                        color={COLOR_CONFIG['neutral-text']}
                        weight={600}
                      >
                        Level {item.level}
                      </Typography>
                    </TagBorder>
                  )}

                  {!isNil(item.lowerBody) && (
                    <TagBorder>
                      <Typography
                        className="text-center"
                        size={12}
                        height={16}
                        color={COLOR_CONFIG['neutral-text']}
                        weight={600}
                      >
                        {item.upperBody}% upper
                      </Typography>
                    </TagBorder>
                  )}

                  {!isNil(item.lowerBody) && (
                    <TagBorder>
                      <Typography
                        className="text-center"
                        size={12}
                        height={16}
                        color={COLOR_CONFIG['neutral-text']}
                        weight={600}
                      >
                        {item.lowerBody}% lower
                      </Typography>
                    </TagBorder>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {!isWarmupOrCooldownOrCheerup && (
          <div style={{ display: 'flex', gap: 10, paddingRight: isEditExercises ? 16 : 0 }}>
            <div style={{ width: 56, paddingTop: '4px' }}>
              {isEditExercises ? (
                <CountInput
                  name="reps"
                  style={{ width: 56 }}
                  value={item.reps}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    onChangeValueSelectedExercises(index, {
                      [e.target.name]: e.target.value,
                    })
                  }
                  type="number"
                  min={1}
                  onKeyPress={handleKeyPress}
                />
              ) : (
                <Typography style={{ fontSize: 18, paddingLeft: '11px', lineHeight: '26px' }}>
                  {item.reps}
                </Typography>
              )}
            </div>
            <div style={{ width: 56, paddingTop: '4px' }}>
              {isEditExercises ? (
                <CountInput
                  name="sets"
                  style={{ width: 56 }}
                  value={item.sets}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    onChangeValueSelectedExercises(index, {
                      [e.target.name]: e.target.value,
                    });
                  }}
                  type="number"
                  min={1}
                  onKeyPress={handleKeyPress}
                />
              ) : (
                <Typography style={{ fontSize: 18, paddingLeft: '11px', lineHeight: '26px' }}>
                  {item.sets}
                </Typography>
              )}
            </div>
            <div style={{ width: 76, paddingTop: '4px' }}>
              {isEditExercises ? (
                <CountInput
                  name="duration"
                  value={item.restTime}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    onChangeValueSelectedExercises(index, {
                      [e.target.name]: e.target.value,
                    });
                  }}
                  type="number"
                  min={1}
                  onKeyPress={handleKeyPress}
                />
              ) : (
                <Typography style={{ fontSize: 18, paddingLeft: '11px', lineHeight: '26px' }}>
                  {item.restTime}
                </Typography>
              )}
            </div>
            <div style={{ width: 56, paddingTop: '4px' }}>
              {isEditExercises ? (
                <CountInput
                  name="restTime"
                  style={{ width: 56 }}
                  value={item.duration}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    onChangeValueSelectedExercises(index, {
                      [e.target.name]: e.target.value,
                    })
                  }
                  type="number"
                  min={1}
                  onKeyPress={handleKeyPress}
                />
              ) : (
                <Typography style={{ fontSize: 18, paddingLeft: '11px', lineHeight: '26px' }}>
                  {item.duration}
                </Typography>
              )}
            </div>
          </div>
        )}

        {!isWarmupOrCooldownOrCheerup && isEditExercises && (
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', padding: '6px 0 0 0 ' }}>
            {/* <DuplicateIcon style={{ cursor: 'pointer', width: 32 }} /> */}
            <CloseIcon style={{ cursor: 'pointer', width: 32 }} onClick={() => onDelete(index)} />
          </div>
        )}
      </Body>
    );
  };

  return (
    <div style={{ overflow: 'auto' }}>
      <div style={{ minWidth: 'max-content' }}>
        <ExerxerciseTableHeader isEditExercises={isEditExercises} />
        {exercises.length ? (
          <div style={{ paddingBottom: isEditExercises ? 46 : 0, minWidth: '100%' }}>
            <DragDropContainer onDragEnd={onDragEnd}>
              {exercises.map((item, index) => (
                <Draggable
                  key={item.id + index}
                  draggableId={item.id + index}
                  index={index}
                  isDragDisabled={
                    item.category.key === ExerciseContentCategory.WARMUP ||
                    item.category.key === ExerciseContentCategory.COOLDOWN ||
                    !isEditExercises
                  }
                >
                  {(providedItem) => (
                    <div
                      ref={providedItem.innerRef}
                      {...providedItem.draggableProps}
                      {...providedItem.dragHandleProps}
                    >
                      {renderBodyTable(item, index)}
                    </div>
                  )}
                </Draggable>
              ))}
            </DragDropContainer>
          </div>
        ) : (
          <></>
        )}
      </div>
    </div>
  );
};
